import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { supabase } from '../../lib/supabase';
import { functionErrorMessage } from '../../lib/functionError';
import { getPlanDisplayName } from '../../lib/plans';
import { isLocked, planInEffect } from '../../lib/workspaces';
import { metaCookies } from '../../lib/metaPixel';
import { BillingToggle, PricingCards } from '../../components/PricingCards';
import { useToast } from '../../components/Toast';
import { DowngradeModal } from '../../components/DowngradeModal';

const getOrdinalSuffix = (value: number) => {
  const mod100 = value % 100;
  if (mod100 >= 11 && mod100 <= 13) return 'th';
  const mod10 = value % 10;
  if (mod10 === 1) return 'st';
  if (mod10 === 2) return 'nd';
  if (mod10 === 3) return 'rd';
  return 'th';
};

const formatLongDateWithOrdinal = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const day = date.getDate();
  const month = new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(date);
  const year = date.getFullYear();
  return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
};

export function Plans() {
  const [isYearly, setIsYearly] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalActionLoading, setPortalActionLoading] = useState<string | null>(null);
  const [downgradeModalOpen, setDowngradeModalOpen] = useState(false);
  const [downgradeTarget, setDowngradeTarget] = useState<'starter' | 'growth' | 'scale' | 'free'>('free');
  const [downgradeLoading, setDowngradeLoading] = useState(false);
  const [cancelPendingChangeLoading, setCancelPendingChangeLoading] = useState(false);
  const [pendingDowngradeDate, setPendingDowngradeDate] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, workspace, viewingAs, fetchProfile } = useAuthStore();
  const toast = useToast();
  const setWorkspace = useAuthStore((state) => state.setWorkspace);

  // A Locked workspace has no live subscription, even if its old plan is still stored (payment
  // failure leaves plan set), so nothing is current and every plan starts a fresh checkout.
  // A Comped workspace has no Subscription either: buying one ends the comp's effect.
  const locked = isLocked(workspace);
  const comped = !!workspace?.comped;
  const noSubscription = locked || comped;
  const currentPlan = noSubscription ? 'free' : workspace?.plan || 'free';
  const subscriptionStatus = workspace?.subscription_status || 'none';
  const billingInterval = noSubscription ? null : workspace?.billing_interval || null;
  const pendingPlan = noSubscription ? null : workspace?.pending_plan || null;
  const pendingBillingInterval = workspace?.pending_billing_interval || null;
  const isPendingMonthlySwitch = subscriptionStatus === 'switching_to_monthly';
  
  // Check if there's a pending downgrade
  const hasPendingDowngrade =
    subscriptionStatus === 'canceling' ||
    subscriptionStatus === 'downgrading' ||
    isPendingMonthlySwitch;
  const pendingDowngradeTo = subscriptionStatus === 'canceling' ? 'free' : pendingPlan;
  const pendingDowngradeDateLabel = pendingDowngradeDate ? formatLongDateWithOrdinal(pendingDowngradeDate) : null;
  const pendingPlanLabel = pendingDowngradeTo
    ? `${getPlanDisplayName(pendingDowngradeTo)}${pendingDowngradeTo !== 'free' && pendingBillingInterval ? ` (${pendingBillingInterval})` : ''}`
    : null;

  // Check if the current view matches the user's billing interval
  const isCurrentBillingView = currentPlan === 'free' || 
    (billingInterval === 'yearly' && isYearly) || 
    (billingInterval === 'monthly' && !isYearly);

  // Plan hierarchy for upgrade/downgrade detection
  // DB keys: starter=Starter, growth=Growth, scale=Scale
  const PLAN_HIERARCHY: Record<string, number> = {
    free: 0,
    starter: 1,
    growth: 2,
    scale: 3,
  };

  const getButtonText = (targetPlan: string) => {
    // If this is the current plan AND we're on the matching billing interval view
    if (currentPlan === targetPlan && isCurrentBillingView) {
      return 'Current';
    }
    
    // If this is the plan we're downgrading to
    // For Free plan, show on both views since Free has no billing interval
    // For other plans, only show on matching billing view
    const pendingTargetMatchesView =
      targetPlan === 'free' ||
      (pendingBillingInterval ? pendingBillingInterval === (isYearly ? 'yearly' : 'monthly') : isCurrentBillingView);

    if (pendingDowngradeTo === targetPlan && pendingTargetMatchesView) {
      return 'Downgrade Scheduled';
    }
    
    // Upgrade/downgrade logic considering both plan AND billing interval
    const targetPlanLevel = PLAN_HIERARCHY[targetPlan];
    const currentPlanLevel = PLAN_HIERARCHY[currentPlan];
    const targetIsYearly = isYearly; // The view we're looking at
    const currentIsYearly = billingInterval === 'yearly';
    const billingIntervalChanging = targetIsYearly !== currentIsYearly && currentPlan !== 'free';
    
    // Free plan is always "Downgrade" when coming from a paid plan (unless pending)
    if (targetPlan === 'free' && pendingDowngradeTo !== 'free') {
      return 'Downgrade';
    }
    
    // If on Free plan, everything else is "Upgrade"
    if (currentPlan === 'free') {
      return 'Upgrade';
    }
    
    // If billing interval is changing (Monthly → Yearly or vice versa)
    if (billingIntervalChanging) {
      if (targetIsYearly && !currentIsYearly) {
        // Monthly → Yearly
        if (targetPlanLevel > currentPlanLevel) {
          return 'Upgrade'; // Higher tier yearly
        }
        return 'Switch to yearly'; // Same or lower tier yearly
      } else if (!targetIsYearly && currentIsYearly) {
        // Yearly → Monthly
        if (targetPlanLevel > currentPlanLevel) {
          return 'Upgrade'; // Higher tier monthly
        }
        return 'Switch to monthly'; // Same or lower tier monthly
      }
    }
    
    // Same billing interval - use plan hierarchy
    if (targetPlanLevel > currentPlanLevel) {
      return 'Upgrade';
    } else if (targetPlanLevel < currentPlanLevel) {
      return 'Downgrade';
    }
    
    return 'Upgrade'; // Default fallback
  };

  // Track if we've already processed URL params to prevent double toast
  const hasProcessedParams = useRef(false);

  // Handle URL parameters for checkout/portal returns
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    const fromPortal = searchParams.get('from_portal');

    // Only process if we have a param to handle and haven't processed yet
    if (!success && !canceled && !fromPortal) return;
    if (hasProcessedParams.current) return;

    // Mark as processed
    hasProcessedParams.current = true;

    // Clear params to prevent re-processing on re-render
    setSearchParams({}, { replace: true });

    if (success === 'true') {
        toast.success('Subscription updated successfully!');
      // Refresh workspace data to get updated plan state
      if (user) {
        fetchProfile(user.id);
      }
    } else if (canceled === 'true') {
      toast.error('Checkout was cancelled.');
    } else if (fromPortal === 'true') {
      // Returning from Stripe Portal does not guarantee that a billing change
      // was confirmed, so refresh state silently instead of showing a success toast.
      if (user) {
        fetchProfile(user.id);
      }
    }
  }, [searchParams, setSearchParams, toast, user, fetchProfile]);

  useEffect(() => {
    const loadPendingDowngradeDate = async () => {
      if (!workspace?.id || !hasPendingDowngrade) {
        setPendingDowngradeDate(null);
        return;
      }

      const query = supabase
        .from('subscriptions')
        .select('current_period_end')
        .eq('workspace_id', workspace.id)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const { data, error } = await query;

      if (error) {
        console.error('Failed to load pending downgrade date:', error);
        setPendingDowngradeDate(null);
        return;
      }

      setPendingDowngradeDate(data?.current_period_end ?? null);
    };

    loadPendingDowngradeDate();
  }, [workspace?.id, hasPendingDowngrade]);

  useEffect(() => {
    if (billingInterval === 'yearly') {
      setIsYearly(true);
    }
  }, [billingInterval]);

  const handleCheckout = async (plan: 'starter' | 'growth' | 'scale') => {
    if (!user || !workspace) {
      toast.error('Please log in to upgrade your plan');
      return;
    }

    setCheckoutLoading(plan);

    try {
      const payload = {
        plan,
        billingInterval: isYearly ? 'yearly' : 'monthly',
        userId: user.id,
        email: user.email,
        workspaceId: workspace.id,
        successUrl: `${window.location.origin}/plans?success=true`,
        cancelUrl: `${window.location.origin}/plans?canceled=true`,
        ...metaCookies(),
      };

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: payload,
      });

      if (error) throw new Error(await functionErrorMessage(error));

      if (data?.url) {
          window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const openPortalSession = async (
    loadingKey?: string,
    options?: {
      flowType?: 'subscription_cancel' | 'subscription_update_confirm';
      targetPlan?: 'starter' | 'growth' | 'scale';
      targetBillingInterval?: 'monthly' | 'yearly';
    },
  ) => {
    if (!user) {
      toast.error('Please log in to manage your billing details');
      return;
    }

    if (loadingKey) {
      setPortalActionLoading(loadingKey);
    } else {
      setPortalLoading(true);
    }

    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: {
          userId: user.id,
          workspaceId: workspace?.id,
          returnUrl: `${window.location.origin}/plans?from_portal=true`,
          flowType: options?.flowType,
          targetPlan: options?.targetPlan,
          targetBillingInterval: options?.targetBillingInterval,
        },
      });

      if (error) throw new Error(await functionErrorMessage(error));

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (error: any) {
      console.error('Portal error:', error);
      toast.error(error.message || 'Failed to open billing portal. Please try again.');
    } finally {
      if (loadingKey) {
        setPortalActionLoading(null);
      } else {
        setPortalLoading(false);
      }
    }
  };

  const handleManageSubscription = async () => {
    await openPortalSession();
  };

  const handlePortalPlanChange = async (plan: 'starter' | 'growth' | 'scale') => {
    await openPortalSession(plan, {
      flowType: 'subscription_update_confirm',
      targetPlan: plan,
      targetBillingInterval: isYearly ? 'yearly' : 'monthly',
    });
  };

  const openDowngradeModal = (targetPlan: 'starter' | 'growth' | 'scale' | 'free') => {
    setDowngradeTarget(targetPlan);
    setDowngradeModalOpen(true);
  };

  // Handle click on a plan that could be downgrade, switch, or checkout
  const handlePlanClick = (targetPlan: 'starter' | 'growth' | 'scale' | 'free') => {
    openDowngradeModal(targetPlan);
  };

  const handleDowngrade = async () => {
    if (!user || !workspace) {
      toast.error('Please log in to manage your subscription');
      return;
    }

    setDowngradeLoading(true);

    try {
      setDowngradeModalOpen(false);

      if (downgradeTarget === 'free') {
        const { data, error } = await supabase.functions.invoke('schedule-downgrade', {
          body: {
            userId: user.id,
            workspaceId: workspace.id,
            targetPlan: downgradeTarget,
            billingInterval: isYearly ? 'yearly' : 'monthly',
          },
        });

        if (error) throw new Error(await functionErrorMessage(error));
        if (!data?.success) throw new Error(data?.error || 'Failed to schedule downgrade');

        if (workspace) {
          setWorkspace({
            ...workspace,
            subscription_status: 'canceling',
            pending_plan: 'free',
            pending_billing_interval: null,
          });
        }
        setPendingDowngradeDate(
          data?.cancelAt ? new Date(data.cancelAt * 1000).toISOString() : null
        );
        toast.success(data.message || 'Downgrade scheduled successfully.');
        await fetchProfile(user.id);
        return;
      }

      const { data, error } = await supabase.functions.invoke('change-subscription', {
        body: {
          userId: user.id,
          workspaceId: workspace.id,
          plan: downgradeTarget,
          billingInterval: isYearly ? 'yearly' : 'monthly',
        },
      });

      if (error) throw new Error(await functionErrorMessage(error));
      if (!data?.success) throw new Error(data?.error || 'Failed to change subscription');

      if (workspace) {
        if (data?.scheduled) {
          const nextStatus =
            currentPlan === downgradeTarget && billingInterval === 'yearly' && !isYearly
              ? 'switching_to_monthly'
              : 'downgrading';

          setWorkspace({
            ...workspace,
            subscription_status: nextStatus,
            pending_plan: downgradeTarget,
            pending_billing_interval: isYearly ? 'yearly' : 'monthly',
          });
          setPendingDowngradeDate(
            data?.effectiveAt ? new Date(data.effectiveAt * 1000).toISOString() : null
          );
        }
      }
      toast.success(data.message || 'Subscription updated successfully.');
      await fetchProfile(user.id);
    } catch (error: any) {
      console.error('Downgrade error:', error);
      toast.error(error.message || 'Failed to update subscription. Please try again.');
    } finally {
      setDowngradeLoading(false);
    }
  };

  const handleCancelPendingChange = async () => {
    if (!user || !workspace) {
      toast.error('Please log in to manage your subscription');
      return;
    }

    setCancelPendingChangeLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('cancel-downgrade', {
        body: {
          userId: user.id,
          workspaceId: workspace.id,
        },
      });

      if (error) throw new Error(await functionErrorMessage(error));
      if (!data?.success) throw new Error(data?.error || 'Failed to cancel scheduled change');

      if (workspace) {
        setWorkspace({
          ...workspace,
          subscription_status: 'active',
          pending_plan: null,
          pending_billing_interval: null,
        });
      }
      setPendingDowngradeDate(null);
      toast.success(data.message || 'Scheduled change cancelled.');
      await fetchProfile(user.id);
    } catch (error: any) {
      console.error('Cancel pending change error:', error);
      toast.error(error.message || 'Failed to cancel scheduled change. Please try again.');
    } finally {
      setCancelPendingChangeLoading(false);
    }
  };

  const renderPlanButton = (dbKey: string, checkoutKey?: 'starter' | 'growth' | 'scale') => {
    const isCurrent = currentPlan === dbKey && isCurrentBillingView;
    const isPending =
      pendingDowngradeTo === dbKey &&
      (dbKey === 'free' || (pendingBillingInterval ? pendingBillingInterval === (isYearly ? 'yearly' : 'monthly') : isCurrentBillingView));
    const currentLevel = PLAN_HIERARCHY[currentPlan] ?? 0;
    const targetLevel = PLAN_HIERARCHY[dbKey] ?? 0;
    const isDowngrade = targetLevel < currentLevel;
    const isSamePlanBillingSwitch = currentPlan === dbKey && currentPlan !== 'free' && !isCurrentBillingView;

    const portalKey = checkoutKey ?? (dbKey === 'free' ? 'free' : dbKey);
    const isLoading = checkoutLoading === checkoutKey || downgradeLoading || portalActionLoading === portalKey;

    return (
      <button
        className={`button is-wide ${isCurrent ? 'is-current' : ''} ${isPending ? 'is-pending' : ''}`}
        disabled={isCurrent || isLoading || isPending}
        onClick={() => {
          if (isSamePlanBillingSwitch) {
            openDowngradeModal(dbKey as 'starter' | 'growth' | 'scale');
            return;
          }

          if ((currentPlan === dbKey && hasPendingDowngrade) || isCurrent) {
            return;
          }

          if (currentPlan === 'free' && checkoutKey) {
            handleCheckout(checkoutKey);
            return;
          }

          if (currentPlan !== 'free' && checkoutKey && targetLevel > currentLevel) {
            handlePortalPlanChange(checkoutKey);
            return;
          }

          if (currentPlan !== 'free' && (isDowngrade || dbKey === 'free')) {
            handlePlanClick(dbKey as 'starter' | 'growth' | 'scale' | 'free');
          }
        }}
      >
        {isLoading ? 'Loading...' : getButtonText(dbKey)}
      </button>
    );
  };

  return (
    <div className="section-application">
      <div className="container-medium is-subpage">
        <div className="top-header">
          <div>
            <h1>Pricing Plans</h1>
            <div className="spacer-tiny"></div>
            <div className="text-size-regular text-colour-alt-grey max-width-small">
              You can review plans on this page, and manage upgrades, downgrades, cancellations, and billing details in Stripe{' '}
              {!viewingAs && <a
                href="#"
                className="dark-link"
                onClick={(e) => { e.preventDefault(); handleManageSubscription(); }}
                style={{ opacity: portalLoading ? 0.6 : 1, pointerEvents: portalLoading ? 'none' : 'auto' }}
              >
                {portalLoading ? 'Loading...' : 'here'}
              </a>}
            </div>
          </div>
          <BillingToggle isYearly={isYearly} onChange={setIsYearly} />
        </div>
        <div className="spacer-large"></div>

        <PricingCards isYearly={isYearly} renderButton={(plan) => (viewingAs ? null : renderPlanButton(plan, plan))} />

        <div className="spacer-tiny"></div>
        <div
          className="text-size-xsmall text-colour-alt-grey"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            whiteSpace: 'nowrap',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <span className="text-weight-medium">
              Current Plan: {locked ? 'None' : getPlanDisplayName(planInEffect(workspace))}{currentPlan !== 'free' && billingInterval ? ` (${billingInterval})` : ''}
            </span>
            {comped &&
              ` · Complimentary${workspace?.comped_until ? ` until ${formatLongDateWithOrdinal(workspace.comped_until)}` : ''}`}
            {pendingDowngradeDateLabel
              ? isPendingMonthlySwitch
                ? ` · Switches to ${pendingPlanLabel} ${pendingDowngradeDateLabel}`
                : hasPendingDowngrade
                  ? ` · Downgrades to ${pendingPlanLabel} ${pendingDowngradeDateLabel}`
                  : ''
              : ''}
          </div>
          {hasPendingDowngrade && !viewingAs ? (
            <a
              href="#"
              className="small-link"
              onClick={(e) => {
                e.preventDefault();
                if (!cancelPendingChangeLoading) {
                  handleCancelPendingChange();
                }
              }}
              style={{
                opacity: cancelPendingChangeLoading ? 0.6 : 1,
                pointerEvents: cancelPendingChangeLoading ? 'none' : 'auto',
                marginLeft: 'auto',
                marginRight: '0rem',
                flexShrink: 0,
                textAlign: 'right',
              }}
            >
              {cancelPendingChangeLoading
                ? 'Cancelling...'
                : isPendingMonthlySwitch
                  ? 'Cancel scheduled change'
                  : 'Cancel downgrade'}
            </a>
          ) : null}
        </div>

        <div className="spacer-huge"></div>
      </div>

      <DowngradeModal
        isOpen={downgradeModalOpen}
        onClose={() => setDowngradeModalOpen(false)}
        onConfirm={handleDowngrade}
        currentPlan={currentPlan}
        targetPlan={downgradeTarget}
        currentBillingInterval={billingInterval}
        targetBillingInterval={downgradeTarget === 'free' ? null : (isYearly ? 'yearly' : 'monthly')}
        loading={downgradeLoading}
      />

      <div className="spacer-large"></div>
    </div>
  );
}
